/**
 * Lunar Calendar Cell Component
 * Custom date cell for react-big-calendar that displays both solar and lunar dates
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLunarDateDisplay, isVietnameseHoliday, getDayLuck, getHolidayName } from '../../utils/lunarUtils';
import { useCalendarEvents } from '../../contexts/CalendarEventsContext';

const LunarCalendarCell = ({ date, label, onDrillDown }) => {
  const { t, i18n } = useTranslation('calendar');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  // React-big-calendar passes 'date' not 'value' for dateHeader component
  const { eventsByDate } = useCalendarEvents();

  // Validate the date
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return (
      <div className="calendar-cell-header">
        <div className="solar-date">{label}</div>
      </div>
    );
  }

  // Get lunar date for this solar date
  const lunarDateStr = getLunarDateDisplay(date, 'date_only'); // Use short format

  // Check if it's a Vietnamese holiday + classify hoàng đạo / hắc đạo
  const holiday = isVietnameseHoliday(date);
  const luck = getDayLuck(date);  // 'hoang_dao' | 'hac_dao' | null

  // Get events for this date from context
  const dateKey = date.toDateString();
  const dateEvents = eventsByDate[dateKey] || [];

  // Count service/other events (excluding payment/lunar)
  const serviceEventCount = dateEvents.filter(event => {
    if (!event.resource) return false;
    const eventType = event.resource.event_type;
    return eventType !== 'payment_due' && eventType !== 'lunar_holiday';
  }).length;

  const isToday = new Date().toDateString() === date.toDateString();

  return (
    <div
      className={`calendar-cell-header ${isToday ? 'is-today' : ''}`}
      onClick={onDrillDown}
      style={{ cursor: 'pointer' }}
    >
      <div className="date-row">
        {/* Top-left: lunar date + luck dot */}
        <div className="lunar-corner">
          <div className={`lunar-date ${holiday ? 'lunar-holiday-text' : ''}`} title={lunarDateStr}>
            {lunarDateStr}
          </div>
          {luck && (
            <span
              className={`luck-dot luck-${luck}`}
              title={luck === 'hoang_dao' ? t('cell.luckHoangDao') : t('cell.luckHacDao')}
              aria-label={luck === 'hoang_dao' ? t('astro.hours.good') : t('astro.hours.bad')}
            />
          )}
        </div>

        {/* Top-right: solar date number */}
        <div className="solar-date" title={date.toLocaleDateString()}>
          {label}
        </div>
      </div>

      {serviceEventCount > 0 && (
        <div className="event-count-placeholder" title={t('cell.otherEvents', { count: serviceEventCount })}>
          +{serviceEventCount}
        </div>
      )}

      {holiday && (
        <div
          className="holiday-badge"
          title={getHolidayName(holiday, lang)}
          style={{ backgroundColor: holiday.color || 'var(--calendar-holiday)' }}
        >
          {getHolidayName(holiday, lang)}
        </div>
      )}
    </div>
  );
};

export default LunarCalendarCell;
